import React, { lazy, Suspense, useState } from 'react';
import Imgix from 'react-imgix';
import ReactLoading from 'react-loading';
import { animateScroll as scroll } from 'react-scroll';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import styled, { createGlobalStyle } from 'styled-components';
import LoadingScreen from '../../../../../../common/LoadingScreen';
import LeftArrow from '../../../../../../common/svgs/LeftArrow';
import RightArrow from '../../../../../../common/svgs/RightArrow';
import { getImgixUrl, getName, getPrice } from '@/utils/helper';
import Heart from '../../../../common/greyHeart/Heart';
import { isQuickShip } from '@/common/QuickShip';
import QuickShipBadge from '@/components/customers/common/QuickShipBadge';
const ZoomImageModal = lazy(() => import('./ZoomImageModal'));

const Wrapper = styled.div`
  width: 100%;

  li.slick-active button:before {
    color: ${({ theme }) => theme.colors.french};
    opacity: 1;
    font-size: 8px;
  }

  .slick-next {
    right: 35px;

    &:before {
      display: none;
    }
  }

  .slick-dots {
    bottom: 15px;
  }

  .slick-prev {
    z-index: 1;
    left: 35px;
    &:before {
      display: none;
    }
  }
  .slick-arrow {
    top: 40%;

    svg {
      width: 16px;
      path {
        stroke-width: 3px;
      }
    }
  }

  .image-container {
    position: relative;
    i {
      color: #656565;
      font-size: 24px;
      position: absolute;
      top: 18px;
      left: 18px;
      cursor: pointer;
    }
  }

  .quick-ship-badge {
    top: 5px;
    left: 42px;
  }
`;

const GlobalStyle = createGlobalStyle`
  html > body {
    overflow: ${({ open }) => (open ? 'hidden' : 'auto')};
    overflow-x: hidden;
  }

  #navigation {
    visibility: hidden;
  }

  .extole-overlay-cta-wrapper {
    display: ${({ open }) => (open ? 'none' : 'auto')};
  }

  #gorgias-chat-container {
    display: ${({ open }) => (open ? 'none' : 'auto')};
  }

  #nav-banner-mobile {
    visibility: ${({ open }) => (open ? 'hidden' : 'visible')};
    display: ${({ open }) => (open ? 'none' : 'auto')};
    transition: all 1s;
  }
`;

function ImageSliderMobile({ productData, sliderImages, loading, inView: isStandardGalleryInView }) {
  const [open, setOpen] = useState(false);
  const [lazyLoadOnClick, setLazyLoadOnClick] = useState();
  const [zoomedUrl, setZoomedUrl] = useState({ src: null });

  const toggle = () => {
    setZoomedUrl({ src: null });
    setOpen(false);
  };

  const handleOpenModal = image => {
    setLazyLoadOnClick(true);
    setZoomedUrl(image);
    setOpen(true);
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />
  };

  const scrollToTop = () => {
    scroll.scrollToTop();
  };

  const imagesWithoutFirst = sliderImages.filter(x => x.src != zoomedUrl.src);
  const formattedImages = [zoomedUrl, ...imagesWithoutFirst].map(image => {
    return {
      src: `${getImgixUrl(image.src)}?auto=format&compress`,
      w: image.width,
      h: image.height,
      title: productData.name
    };
  });

  return (
    <Wrapper>
      {isQuickShip(productData) && isStandardGalleryInView && (
        <QuickShipBadge width="80px" className="quick-ship-badge" />
      )}
      <StickyProductOverlay isVisible={!isStandardGalleryInView} >
        <SliderItem onClick={scrollToTop}>
          <div className="image-container">
            <Imgix
              imgixParams={{ auto: 'compress,format' }}
              className="product-image"
              sizes="80vw"
              src={getImgixUrl(sliderImages[0].src)}
              htmlAttributes={{
                alt: sliderImages[0].name,
                loading: 'lazy'
              }}
            />
          </div>
        </SliderItem>
        <div className="price-title">
          <ProductTitle>{getName(productData.name)}</ProductTitle>
          <Price>${getPrice(productData.price)}</Price>
          <div className="heart-container">
            <Heart
              productName={productData.name}
              productSlug={productData.slug}
              productId={productData.id}
            />
          </div>
        </div>
      </StickyProductOverlay>
      <StandardGallery>
        {!loading && (
          <div className="heart-container">
            <Heart
              productName={productData.name}
              productSlug={productData.slug}
              productId={productData.id}
            />
          </div>
        )}
        <Slider {...settings}>
          {sliderImages.map((image, i) => {
            return (
              <SliderItem key={i}>
                <div className="image-container">
                  {loading ? (
                    <ReactLoading type="spin" color="#2a5caf" />
                  ) : (
                    <Imgix
                      imgixParams={{ auto: 'compress,format' }}
                      className="product-image"
                      sizes="80vw"
                      src={getImgixUrl(image.src)}
                      htmlAttributes={{
                        alt: productData.name,
                        loading: 'lazy'
                      }}
                    />
                  )}
                  {/* <i
                    className="fas fa-search-plus"
                    onClick={() => handleOpenModal(image)}
                  /> */}
                </div>
              </SliderItem>
            );
          })}
        </Slider>
      </StandardGallery>

      <Suspense
        fallback={
          <LoadingWrapper>
            <LoadingScreen />
          </LoadingWrapper>
        }
      >
      </Suspense>
      {open && <GlobalStyle open={open} />}
    </Wrapper>
  );
}

export default ImageSliderMobile;

const LoadingWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;

  .loading-wrapper {
    background: rgba(255, 255, 255, 0.7);
  }
`;

const StandardGallery = styled.div`
  .heart-container {
    z-index: 1;
    width: fit-content;
    position: absolute;
    top: 10px;
    right: 10px;
  }
`;

const PrevArrow = props => {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <LeftArrow />
    </div>
  );
};

const NextArrow = props => {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <RightArrow />
    </div>
  );
};

const StickyProductOverlay = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  border-bottom: 1px solid #d8d8d8;
  grid-gap: 5px;

  @media (max-width: 992px) {
    display: ${({ isVisible }) => (isVisible ? 'grid' : 'none')};
    position: fixed;
    width: 100%;
    top: 60px;
    left: 0;
    z-index: 5;
    background: #fff;
    border-top: 1px solid #d8d8d8;
  }

  .price-title {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    .heart-container {
      width: fit-content;
      position: absolute;
      bottom: 10px;
      right: 10px;
    }
  }
`;

const Price = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => (theme.old ? 'bold' : 600)};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.12em;
`;
const ProductTitle = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 55px);

  font-family: ${({ theme }) => theme.fonts.secondary.family};
  letter-spacing: ${({ theme }) => theme.fonts.secondary.letterSpacing};
  font-style: normal;
  font-weight: ${({ theme }) => (theme.old ? 'normal' : '300')};
  font-size: 18px;
  line-height: 22px;
`;

const SliderItem = styled.div`
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
  width: 100%;
  padding-bottom: 56.25%;
  position: relative;
  background: #f7f9fa;
  .image-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    height: 100%;
    position: absolute;
    img {
      height: 80%;
      width: auto;
    }
  }
`;
